import { Mission } from '@/domain/project/Mission';
import { StoreOptions as VuexStoreOptions } from 'vuex';
import { Project } from '@/domain/project/Project';
import { Country } from '@/domain/country/Country';
import { intersect } from '@/domain/ArrayUtils';
import { CountryLabel } from '@/domain/country/CountryLabel';
import { GeoJSON } from 'ol/format';
import { ResearchUnit } from '@/domain/research-unit/ResearchUnit';

export interface State {
  countries: Country[];
  countryLabelFeatureCollection: GeoJSON.FeatureCollection<GeoJSON.Point, CountryLabel>;
  projects: Project[];
  researchUnits: ResearchUnit[];
  missionFilter: Mission[];
  departmentFilter: string;
  researchUnitFilter?: number;
}

export const storeOptions: VuexStoreOptions<State> = {
  state: {
    countries: [],
    countryLabelFeatureCollection: { type: 'FeatureCollection', features: [] },
    projects: [],
    researchUnits: [],
    missionFilter: [Mission.ENSEIGNEMENT, Mission.COLLECTIONS, Mission.EXPERTISE, Mission.DIFFUSION, Mission.RECHERCHE],
    departmentFilter: 'ALL',
    researchUnitFilter: undefined,
  },
  mutations: {
    saveCountries(state: State, countries: Country[]) {
      state.countries = countries;
    },
    saveCountryLabelFeatureCollection(state: State, countryLabelFeatureCollection: GeoJSON.FeatureCollection<GeoJSON.Point, CountryLabel>) {
      state.countryLabelFeatureCollection = countryLabelFeatureCollection;
    },
    saveProjects(state: State, projects: Project[]) {
      state.projects = projects;
    },
    saveResearchUnits(state: State, researchUnits: ResearchUnit[]) {
      state.researchUnits = researchUnits;
    },
    saveMissionFilter(state: State, missions: Mission[]) {
      state.missionFilter = missions;
    },
    saveDepartmentFilter(state: State, department) {
      state.departmentFilter = department;
    },
    saveResearchUnitFilter(state: State, researchUnitId?) {
      state.researchUnitFilter = researchUnitId;
    },
  },
  getters: {
    researchUnitsByDepartment: (state: State) => {
      return state.researchUnits.reduce((accumulator: { [key: string]: number[] }, researchUnit: ResearchUnit) => {
        const researchUnits = accumulator[researchUnit.department] || [];
        return { ...accumulator, [researchUnit.department]: [...researchUnits, researchUnit.id] };
      }, {});
    },
    missionFilteredProjects: (state: State) => {
      return state.projects.filter(project => intersect(state.missionFilter, project.missions));
    },
    departmentFilteredProjects: (state: State, getters: any) => {
      const departmentFilter = (project: Project) =>
        intersect(getters.researchUnitsByDepartment[state.departmentFilter], project.researchUnits);
      return state.departmentFilter !== 'ALL' ? getters.missionFilteredProjects.filter(departmentFilter) : getters.missionFilteredProjects;
    },
    filteredProjects: (state: State, getters: any): Project[] => {
      return state.researchUnitFilter
        ? getters.departmentFilteredProjects.filter((project: any) => project.researchUnits.indexOf(state.researchUnitFilter!) > -1)
        : getters.departmentFilteredProjects;
    },
    projectCountsByCountry: (state: State, getters: any): { [key: string]: number } => {
      return getters.filteredProjects.reduce((countries: { [key: string]: number }, project: Project) => {
        project.countries.forEach(country => (countries[country] = countries[country] + 1 || 1));
        return countries;
      }, {});
    },
    filteredResearchUnits: (state: State, getters: any): ResearchUnit[] => {
      return state.departmentFilter !== 'ALL'
        ? state.researchUnits
            .filter(researchUnit => getters.researchUnitsByDepartment[state.departmentFilter].indexOf(researchUnit.id) > -1)
            .sort((researchUnit1, researchUnit2) => (researchUnit1.name < researchUnit2.name ? -1 : 1))
        : state.researchUnits;
    },
  },
};
